import {
  SkeletonBodyText,
  SkeletonPage,
  Layout,
  LegacyCard,
  TextContainer,
  Banner,
  Page,
} from '@shopify/polaris';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getProdutById,
  updateProduct,
  createVariant,
  updateVariant,
  deleteVariant,
} from '../../../services/productService';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import ProductForm from '../../UI/Products/ProductForm';

const EditProduct = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [, dispatchToast] = useContext(ToastContext);

  const returnMatch = window.location.search.match(/\??r=(.+)&?/);
  const returnUrl = returnMatch && returnMatch.length > 1 ? window.atob(returnMatch[1]) : `/products/${id}`;

  // ---------- Carga de datos ----------
  const {
    data: product,
    isLoading,
    isError,
  } = useQuery(['product', id], () => getProdutById(id).then((r) => r.data), {
    enabled: !!id,
  });

  // ---------- Mutations ----------
  const updateProductMutation = useMutation((data) => updateProduct(id, data).then((r) => r.data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['product', id]);
      dispatchToast({ type: SET_OK, payload: { message: 'Producto actualizado correctamente' } });
    },
    onError: (e) => {
      dispatchToast({ type: SET_ERROR, payload: { message: e.response?.data?.message || 'Error al actualizar' } });
    },
  });

  const updateVariantMutation = useMutation(
    ({ variantId, data }) => updateVariant(id, variantId, data).then((r) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['product', id]);
        dispatchToast({ type: SET_OK, payload: { message: 'Variante actualizada' } });
      },
      onError: (e) => {
        dispatchToast({
          type: SET_ERROR,
          payload: { message: e.response?.data?.message || 'Error al actualizar variante' },
        });
      },
    },
  );

  const createVariantMutation = useMutation((data) => createVariant(id, data).then((r) => r.data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['product', id]);
      dispatchToast({ type: SET_OK, payload: { message: 'Variante creada correctamente' } });
    },
    onError: (e) => {
      dispatchToast({ type: SET_ERROR, payload: { message: e.response?.data?.message || 'Error al crear variante' } });
    },
  });

  const deleteVariantMutation = useMutation((variantId) => deleteVariant(id, variantId).then((r) => r.data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['product', id]);
      dispatchToast({ type: SET_OK, payload: { message: 'Variante eliminada' } });
    },
    onError: (e) => {
      dispatchToast({
        type: SET_ERROR,
        payload: { message: e.response?.data?.message || 'Error al eliminar variante' },
      });
    },
  });

  const handleSubmit = ({ type, data, variantId }) => {
    switch (type) {
      case 'save_product':
        updateProductMutation.mutate(data);
        break;
      case 'create_variant':
        createVariantMutation.mutate(data);
        break;
      case 'update_variant':
        updateVariantMutation.mutate({ variantId, data });
        break;
      case 'delete_variant':
        deleteVariantMutation.mutate(variantId);
        break;
      default:
        console.warn('Unknown action type:', type);
    }
  };

  // ---------- Loading / Error states ----------
  if (isLoading) {
    return (
      <SkeletonPage primaryAction>
        <Layout>
          <Layout.Section>
            <LegacyCard sectioned>
              <TextContainer>
                <SkeletonBodyText lines={8} />
              </TextContainer>
            </LegacyCard>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  if (isError || !product) {
    return (
      <Page title="Error" backAction={{ content: 'Volver', url: returnUrl }}>
        <Banner status="critical">
          <p>No se pudo cargar el producto.</p>
        </Banner>
      </Page>
    );
  }

  return (
    <ProductForm
      title={`Editar: ${product.title}`}
      subtitle={product.family}
      backAction={{ content: 'Ver producto', url: `/products/${id}` }}
      initialData={product}
      onSubmit={handleSubmit}
      isLoading={
        updateProductMutation.isLoading ||
        updateVariantMutation.isLoading ||
        createVariantMutation.isLoading ||
        deleteVariantMutation.isLoading
      }
    />
  );
};

export default EditProduct;
